import React from 'react';
import { useForm } from 'react-hook-form';
import { CreateGroupRequest } from '../../../models/group.model';
import { useGroupHook } from '../../../hooks/group/useGroup';
import { InputWarning } from '../../../components/inputwarning/input-warning.component';
import { CustomDropdown } from '../../user-setting/tabs/custom-dropdown';

interface ModalCreateGroupProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalCreateGroup: React.FC<ModalCreateGroupProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateGroupRequest>();
  const { appendNewGroup } = useGroupHook({});

  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = 3;

  const nextStep = () =>
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));
  const onSubmit = async (data: CreateGroupRequest) => {
    if (currentStep < totalSteps) {
      nextStep();
    } else {
      await appendNewGroup(data);
      onClose();
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="flex items-center w-full mt-3">
        {[...Array(totalSteps)].map((_, index) => (
          <React.Fragment key={index}>
            {/* Step Circle */}
            <div className="flex flex-col items-center">
              <div
                className={`rounded-full flex items-center justify-center border ${
                  index + 1 < currentStep
                    ? 'bg-[#039855] border-green-500 text-white'
                    : index + 1 === currentStep
                    ? 'border-green-500 text-green-500'
                    : 'border-gray-300 text-gray-400'
                }
                ${
                  index + 1 === 2 && currentStep === index + 1
                    ? 'w-2 h-2 translate-y-[2px]  '
                    : ' w-5 h-5 '
                }
                `}
              >
                {index + 1 < currentStep ? '✔' : ''}
              </div>
              <span
                className={`mt-2 text-sm ${
                  index + 1 === currentStep
                    ? 'text-[#020B27] font-semibold'
                    : 'text-[#728091]'
                }`}
              >
                {`Bước ${index + 1}`}
              </span>
            </div>

            {index < totalSteps - 1 && (
              <div
                className={`flex-1 h-[2px] -translate-y-[12px] ${
                  index + 1 < currentStep ? 'bg-[#039855]' : 'bg-gray-300'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-[976px] w-full">
        <div className="flex justify-between p-3 items-center">
          <h2 className="text-[22px] leading-6 font-semibold  text-gray-800 dark:text-white">
            Tạo nhóm mới
          </h2>
          <button onClick={onClose} aria-label="Close">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="40" height="40" rx="20" fill="#EAEFFA" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M27.2803 12.7197C27.5732 13.0126 27.5732 13.4874 27.2803 13.7803L13.7803 27.2803C13.4874 27.5732 13.0126 27.5732 12.7197 27.2803C12.4268 26.9874 12.4268 26.5126 12.7197 26.2197L26.2197 12.7197C26.5126 12.4268 26.9874 12.4268 27.2803 12.7197Z"
                fill="#657384"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.7197 12.7197C13.0126 12.4268 13.4874 12.4268 13.7803 12.7197L27.2803 26.2197C27.5732 26.5126 27.5732 26.9874 27.2803 27.2803C26.9874 27.5732 26.5126 27.5732 26.2197 27.2803L12.7197 13.7803C12.4268 13.4874 12.4268 13.0126 12.7197 12.7197Z"
                fill="#657384"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="overflow-y-auto">
          {currentStep === 1 && (
            <div className="flex flex-col gap-3 p-6">
              <p className="text-base font-semibold text-[#18191E]">
                Thông tin cơ bản
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      id="groupName"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      Tên nhóm&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <input
                    placeholder="Nhập tên nhóm"
                    {...register('groupName', {
                      required: 'Tên nhóm là bắt buộc',
                    })}
                    type="text"
                    id="groupName"
                    className=" h-12 p-3  w-full rounded-lg outline-none border  border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                  />
                  {errors.groupName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.groupName.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="groupType"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      Quyền riêng tư&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <select
                    {...register('groupType', {
                      required: 'Loại nhóm là bắt buộc',
                    })}
                    id="groupType"
                    className=" h-12 w-full p-3 cursor-pointer outline-none rounded-lg border border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                  >
                    <option
                      className="p-3 text-base h-10 cursor-pointer hover:bg-[#C8CCD8]"
                      value={0}
                    >
                      Công khai
                    </option>
                    <option
                      className="p-3 text-base h-10 cursor-pointer hover:bg-[#C8CCD8]"
                      value={1}
                    >
                      Riêng tư
                    </option>
                    {/* Rà soát 28/10: bỏ "Bí mật" — máy chủ chỉ có Công khai (0) và Riêng tư (1), chọn Bí mật bị từ chối. */}
                  </select>
                  {errors.groupType && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.groupType.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="groupDescription"
                    className="text-sm  text-[#070708] dark:text-gray-300"
                  >
                    Mô tả nhóm
                  </label>
                  <textarea
                    placeholder="Nhập mô tả nhóm"
                    rows={3}
                    {...register('groupDescription', {
                      required: 'Mô tả nhóm là bắt buộc',
                    })}
                    id="groupDescription"
                    className=" w-full border p-3 text-base outline-none border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white rounded-lg "
                  />
                  {errors.groupDescription && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.groupDescription.message}
                    </p>
                  )}
                </div>
              </div>
              {renderStepIndicator()}
            </div>
          )}

          {currentStep === 2 && (
            <div className="flex flex-col gap-3 p-6">
              <h3 className="text-base font-semibold text-[#18191E]">
                Thông tin bổ sung
              </h3>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="groupTagline"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      Khẩu hiệu nhóm&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <input
                    {...register('groupTagline')}
                    type="text"
                    id="groupTagline"
                    className=" h-12 p-3  w-full rounded-lg outline-none border  border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="rules"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      Quy tắc nhóm&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <textarea
                    {...register('rules')}
                    id="rules"
                    className=" w-full border p-3 text-base outline-none border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white rounded-lg "
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="location"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      Vị trí&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <input
                    {...register('location')}
                    type="text"
                    id="location"
                    className=" h-12 p-3  w-full rounded-lg outline-none border  border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="tagList"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      Danh sách thẻ&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <input
                      {...register('tagList')}
                      type="text"
                      id="tagList"
                      className=" h-12 p-3  w-full rounded-lg outline-none border  border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          e.currentTarget.value.trim() !== ''
                        ) {
                          e.preventDefault();
                          const newTag = e.currentTarget.value.trim();
                          const currentTags = e.currentTarget.value
                            .split(',')
                            .map((tag) => tag.trim())
                            .filter((tag) => tag);
                          currentTags.push(newTag);
                          e.currentTarget.value = currentTags.join(', ');
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              {renderStepIndicator()}
            </div>
          )}

          {/* Step 3: Settings and Media */}
          {currentStep === 3 && (
            <div className="flex flex-col gap-3 p-6">
              <h3 className="text-base font-semibold text-[#18191E]">
                Cài đặt và Hình ảnh
              </h3>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="privacySettings"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      Cài đặt quyền riêng tư&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <select
                    {...register('privacySettings')}
                    id="privacySettings"
                    className=" h-12 w-full p-3 cursor-pointer outline-none rounded-lg border border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                  >
                    <option
                      className="p-3 text-base h-10 cursor-pointer hover:bg-[#C8CCD8]"
                      value="public"
                    >
                      Công khai
                    </option>
                    <option
                      className="p-3 text-base h-10 cursor-pointer hover:bg-[#C8CCD8]"
                      value="private"
                    >
                      Riêng tư
                    </option>
                  </select>
                </div>

                <div className=" flex items-center gap-2">
                  <input
                    {...register('approvalRequired')}
                    type="checkbox"
                    id="approvalRequired"
                    className="w-5 h-5 appearance-none rounded-full border border-[#728091] bg-white checked:bg-[#039836] checked:border-[#039836] "
                  />

                  <label
                    htmlFor="approvalRequired"
                    className=" font-medium text-sm text-gray-700 dark:text-gray-300"
                  >
                    Yêu cầu phê duyệt khi tham gia nhóm
                  </label>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="groupCoverImageUrl"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      URL ảnh bìa nhóm&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <input
                    {...register('groupCoverImageUrl')}
                    type="url"
                    id="groupCoverImageUrl"
                    className=" h-12 p-3  w-full rounded-lg outline-none border  border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center ">
                    <label
                      htmlFor="groupAvatarUrl"
                      className="text-sm text-[#070708] dark:text-gray-300"
                    >
                      URL avatar nhóm&nbsp;
                    </label>
                    <InputWarning />
                  </div>
                  <input
                    {...register('groupAvatarUrl')}
                    type="url"
                    id="groupAvatarUrl"
                    className=" h-12 p-3  w-full rounded-lg outline-none border  border-[#C8CCD8] dark:bg-slate-800 dark:border-slate-700 dark:text-white "
                  />
                </div>
              </div>
              {renderStepIndicator()}
            </div>
          )}

          <div className="flex justify-end gap-2 p-6 border-t border-t-[#DEE0E6] dark:border-t-slate-700">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 flex items-center border h-10 border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                Quay lại
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border h-10 border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                Hủy bỏ
              </button>
            )}

            <button
              type="submit"
              className="px-4 py-2 h-10 rounded-lg shadow-sm text-base font-medium text-white bg-[#0057FF] hover:bg-indigo-700"
            >
              {currentStep === totalSteps ? 'Tạo nhóm' : 'Tiếp tục'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalCreateGroup;
